import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Spacer,
  Flex,
  Stack,
  VStack,
  FormControl,
  Popover,
  PopoverTrigger,
  PopoverBody,
  PopoverContent,
  Button,
  RadioGroup,
  Radio,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { IoIosArrowDown } from "react-icons/io";
import { MdFilterList } from "react-icons/md";
import { FaCheck, FaCheckDouble } from "react-icons/fa";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import SearchTable2 from "components/Tables/SearchTable2";
import Loading from "components/Loading/Loading";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { SidebarContext } from "contexts/SidebarContext";
import { isAitValidated, isAitInvalidated, isAitValidationRequired, getAitStatusTranslationKey } from "utils/traffic-violation-utils";
import { ROUTES } from "constants/routes-path";
import { PERMISSIONS } from "constants/permission";
import { AllotmentStatus, violationAllotmentsValidationService } from "services/violationAllotmentsValidationService";

const ViolationAllotmentsViolationList = () => {
  const { t } = useTranslation();
  const { allotmentId } = useParams();
  const { isOpen, onToggle } = useDisclosure();
  const { updateBreadcrumbKeyParams } = useContext(SidebarContext);
  const [filterBy, setFilterBy] = useState("all");
  const [allotment, setAllotment] = useState(undefined);
  const [allotmentViolations, setAllotmentViolations] = useState(undefined);
  const navigate = useNavigate();
  const showToast = useCustomToast();

  const translationKey = "violation-allotments.violations.list";

  const filters = useMemo(() => ({
    all: { id: "all", label: t(`${translationKey}.labels.all`) },
    validated: { id: "validated", label: t(`${translationKey}.labels.validated`) },
    invalidated: { id: "invalidated", label: t(`${translationKey}.labels.invalidated`) },
  }), []);

  const columns = useMemo(() => ([
    { Header: "", accessor: "id", isHidden: true },
    { Header: t(`${translationKey}.table.header.ait-number`), accessor: "numero_ait" },
    { Header: t(`${translationKey}.table.header.owner-name`), accessor: "nome_proprietario" },
    { Header: t(`${translationKey}.table.header.plate`), accessor: "placa" },
    { Header: t(`${translationKey}.table.header.date`), accessor: "data" },
    { Header: t(`${translationKey}.table.header.violation`), accessor: "descricao_infracao" },
    { Header: t(`${translationKey}.table.header.value`), accessor: "valor" },
    {
      Header: t(`${translationKey}.table.header.situation`),
      accessor: "status",
      custom: ({ value }) => t(getAitStatusTranslationKey(value)),
    },
    {
      Header: t("system.labels.actions"),
      disableSortBy: true,
      actions: [
        {
          label: t(`${translationKey}.actions.revalidate`),
          permission: PERMISSIONS.VIOLATION_VALIDATION,
          visible: (row) => !isAitValidationRequired(row.status),
          icon: <FaCheckDouble color="#48BB78" />,
          onClick: ({ original }) => {
            const validatePath = ROUTES.violationAllotmentsValidateViolation
              .replace(":allotmentId", allotmentId)
              .replace(":violationId", original.id);
            navigate(ROUTES.transportManagementPath + validatePath);
          },
        },
        {
          label: t(`${translationKey}.actions.validate`),
          permission: PERMISSIONS.VIOLATION_VALIDATION,
          visible: (row) => isAitValidationRequired(row.status),
          colorScheme: "blue",
          icon: <FaCheck />,
          onClick: ({ original }) => {
            const validatePath = ROUTES.violationAllotmentsValidateViolation
              .replace(":allotmentId", allotmentId)
              .replace(":violationId", original.id);
            navigate(ROUTES.transportManagementPath + validatePath);
          },
        },
      ],
    },
  ]), []);

  const getFilteredViolations = useCallback(() => allotmentViolations.filter(el => {
    switch (filterBy) {
      case filters.validated.id: {
        return isAitValidated(el.status);
      }
      case filters.invalidated.id: {
        return isAitInvalidated(el.status);
      }
      default:
        return true;
    }
  }), [allotmentViolations, filterBy]);

  const handleFilterBy = (value) => {
    onToggle();
    setFilterBy(value);
  }

  const redirectToList = () => {
    navigate(ROUTES.transportManagementPath + ROUTES.violationAllotmentsValidation);
  }

  const fetchAllotment = async () => {
    const data = await violationAllotmentsValidationService.find(allotmentId);
    if (data.status !== AllotmentStatus.OPEN.value) throw "violation-allotments.messages.errors.find";
    return data;
  }

  const fetchData = async () => {
    if (allotmentViolations) setAllotmentViolations(undefined);

    try {
      const allotment = await fetchAllotment();
      const allotmentViolations = await violationAllotmentsValidationService.listViolations(allotmentId);
      updateBreadcrumbKeyParams({ allotmentName: allotment.nome, interpolation: { escapeValue: false } });
      setAllotment(allotment);
      setAllotmentViolations(allotmentViolations);
    } catch (err) {
      showToast({ title: t(err), status: "error" });
      redirectToList();
    }
  }

  useEffect(() => {
    fetchData();
  }, [allotmentId]);

  return (
    <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
      <Stack w="100%" px="30px" spacing="4" justify="space-between" align="center" direction={{ sm: "column", md: "row" }}>
        <Spacer display={{ sm: "none", md: "flex" }} />

        <Popover placement="bottom" isOpen={isOpen} onClose={onToggle}>
          <PopoverTrigger>
            <Button
              fontSize="sm"
              fontWeight="bold"
              variant="light"
              leftIcon={<MdFilterList />}
              rightIcon={<IoIosArrowDown />}
              onClick={onToggle}
            >
              <Text w="100%">
                {t(`${translationKey}.actions.filter-by`, { filterBy: filters[filterBy].label })}
              </Text>
            </Button>
          </PopoverTrigger>

          <PopoverContent w="100%" variant="light" p="5">
            <PopoverBody>
              <FormControl>
                <RadioGroup id="filter-by" name="filter-by" value={filterBy} onChange={handleFilterBy}>
                  <VStack spacing="5" align="start">
                    {Object.values(filters).map((filter, key) => (
                      <Radio key={key} id={filter.id} value={filter.id}>
                        {filter.label}
                      </Radio>
                    ))}
                  </VStack>
                </RadioGroup>
              </FormControl>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Stack>

      <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
        <CardBody>
          {allotment && allotmentViolations ? (
            <SearchTable2 columnsData={columns} tableData={getFilteredViolations()} />
          ) : (
            <Loading />
          )}
        </CardBody>
      </Card>
    </Flex>
  );
}

export default ViolationAllotmentsViolationList;
